// smsThreadsApi — live, per-thread access to the Text Inbox.
//
// The inbox used to poll a Netlify function for the newest 300–500 rows of
// the WHOLE forwardedSms collection every 7 s and group them into threads in
// the browser. A busy evening pushed a customer's order text off the bottom
// of their own thread, and every open tab cost ~400k Firestore reads/hour.
//
// Now every row carries a normalised `threadKey` and an explicit `otp` flag
// (netlify/functions/_sms-thread.js), and `smsThreads/{threadKey}` holds one
// summary per conversation. The browser subscribes directly:
//   • the conversation list  = smsThreads, newest first, paged
//   • an open thread         = forwardedSms where threadKey == key, newest
//                              30 live, older pages fetched on request
// Reads are gated by firestore.rules to the dispatch tier — by RLS in Postgres
// mode, where the same two queries run against sms_threads / forwarded_sms.
// OTP/shortcode texts are kept out of a dispatcher's view by the query itself
// (`seesOtp` adds `otp == false`) — a UI convention, not a database guarantee
// (per Umer, 2026-08-29: rules stay simple).
//
// Same contract as contactsApi: `(…, cb, onError?) => unsubscribe`, no-op
// when Firestore isn't initialised, cache-first thanks to IndexedDB persistence.

import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';

import { db } from './firebase';
import { USE_SUPABASE, supabase, subscribeTable } from './supabaseClient';
import { normalizePhone } from './phone';
import { type ForwardedSms, type ForwardedDirection } from './forwardedSmsApi';
import type { SmsContact } from './contactsApi';

export const THREADS_COL = 'smsThreads';
export const MESSAGES_COL = 'forwardedSms';
export const THREADS_TABLE = 'sms_threads';
export const MESSAGES_TABLE = 'forwarded_sms';
/** Conversations mounted per page of the list.
 *
 *  30, not 100 (Umer, 2026-09-09): the first page is what stands between a
 *  reload and a usable sidebar, and 30 rows fill any screen with room to spare.
 *  Scrolling pages the next 30 through loadMoreThreads, WhatsApp-style, so
 *  nothing is lost — measured against the real schema this is ~12.7 KB on the
 *  first paint instead of ~42 KB. Messages are NOT part of this: a list row
 *  renders `last_body` from the summary, and a conversation's messages are read
 *  only when it is opened. */
export const THREAD_PAGE = 30;
/** Columns the conversation list renders, before migration 0045. */
export const THREAD_COLUMNS_BASE =
  'thread_key,phone,otp,last_message_at,last_message_id,last_body,last_direction,' +
  'last_inbound_at,last_inbound_id,recent_inbound_at,message_count';
/** …and after it, once the imported history lives in this table. */
export const THREAD_COLUMNS = `${THREAD_COLUMNS_BASE},history_only`;

// ── running BEFORE and AFTER migration 0045 ─────────────────────────────────
// 0045 adds sms_threads.history_only and backfills the imported phone history
// into this table. Selecting a column that does not exist is a hard PostgREST
// error (42703) that would take the whole conversation list down, so the
// schema is probed once and the client adapts:
//
//   applied     → one list; history_only rides along with every row
//   not applied → the base columns, and the page falls back to merging the
//                 imported-history index (cached, so the walk is paid once)
//
// One tiny request per tab, remembered for the session. It self-heals the
// moment the migration runs — no redeploy, no flag to flip.
const HISTORY_COL_KEY = 'smsThreads.historyOnly.v1';
let historyColumn: boolean | null = null;

function rememberHistoryColumn(present: boolean): void {
  historyColumn = present;
  try { sessionStorage.setItem(HISTORY_COL_KEY, present ? '1' : '0'); } catch { /* private mode */ }
}

/** True when migration 0045 has been applied to this database. */
export async function hasHistoryOnlyColumn(): Promise<boolean> {
  if (historyColumn !== null) return historyColumn;
  try {
    const cached = sessionStorage.getItem(HISTORY_COL_KEY);
    if (cached !== null) { historyColumn = cached === '1'; return historyColumn; }
  } catch { /* private mode — probe per tab instead */ }
  if (!USE_SUPABASE) { rememberHistoryColumn(false); return false; }
  try {
    const { error } = await supabase().from(THREADS_TABLE).select('history_only').limit(1);
    // 42703 = undefined_column. Any OTHER error (offline, RLS) must NOT read as
    // "the migration is missing": assume applied and let the real query report
    // the real problem, rather than silently starting the slow fallback.
    const missing = !!error && (error.code === '42703' || /history_only/i.test(error.message || ''));
    rememberHistoryColumn(!missing);
  } catch {
    rememberHistoryColumn(true);
  }
  return historyColumn!;
}

/** Test seam. */
export function _resetHistoryColumnProbe(): void {
  historyColumn = null;
  try { sessionStorage.removeItem(HISTORY_COL_KEY); } catch { /* ignore */ }
}
/** Messages live-subscribed per open thread; "Show earlier" pages the rest. */
export const MESSAGE_PAGE = 30;

export interface SmsThreadSummary {
  threadKey: string;
  /** Display identity: E.164 for a NA phone, else the raw sender id. */
  phone: string;
  otp: boolean;
  lastMessageAt: number;
  lastMessageId: string | null;
  lastBody: string;
  lastDirection: ForwardedDirection;
  lastInboundAt: number;
  lastInboundId: string | null;
  /** Newest inbound timestamps (≤10, oldest → newest) — enough for the unread pill. */
  recentInboundAt: number[];
  messageCount: number;
  /** Imported phone history, never texted on this line (migration 0045). The
   *  sidebar renders it differently; everything else treats it as a thread. */
  historyOnly: boolean;
}

type Data = Record<string, unknown>;
type Query = firebase.firestore.Query<firebase.firestore.DocumentData>;

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : Number(v) || 0);
const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));

/** E.164 for a 10-digit key, else the raw id — mirrors displayPhoneFor server-side. */
export function phoneForKey(threadKey: string, raw?: unknown): string {
  return /^\d{10}$/.test(threadKey) ? `+1${threadKey}` : str(raw).trim() || threadKey;
}

/** Shape a raw summary doc. Exported for tests. */
export function normalizeThread(id: string, data: Data): SmsThreadSummary {
  const threadKey = str(data.threadKey) || id;
  return {
    threadKey,
    phone: phoneForKey(threadKey, data.phone),
    otp: data.otp === true,
    lastMessageAt: num(data.lastMessageAt),
    lastMessageId: str(data.lastMessageId) || null,
    lastBody: str(data.lastBody),
    lastDirection: data.lastDirection === 'out' ? 'out' : 'in',
    lastInboundAt: num(data.lastInboundAt),
    lastInboundId: str(data.lastInboundId) || null,
    recentInboundAt: Array.isArray(data.recentInboundAt) ? data.recentInboundAt.map(num).filter(Boolean) : [],
    messageCount: num(data.messageCount),
    historyOnly: data.historyOnly === true,
  };
}

/** Shape a raw message doc into the ForwardedSms the inbox already renders.
 *  `number` is the thread's display phone so every row of a thread agrees,
 *  whatever format the forwarder stored in `from`. Exported for tests. */
export function normalizeMessage(id: string, data: Data): ForwardedSms {
  const direction: ForwardedDirection = data.direction === 'out' ? 'out' : 'in';
  const party = str(direction === 'out' ? data.to : data.from);
  const threadKey = str(data.threadKey) || normalizePhone(party) || party.trim().toLowerCase();
  return {
    id,
    direction,
    number: phoneForKey(threadKey, party),
    from: party || threadKey,
    body: str(data.body),
    sim: str(data.sim) || null,
    receivedAt: num(data.receivedAt) || num(data.loggedAt),
    routedTo: str(data.routedTo) || null,
    deliveryStatus: direction === 'out'
      ? ((data.deliveryStatus === 'queued' || data.deliveryStatus === 'failed') ? data.deliveryStatus : 'sent')
      : null,
    deliveryError: direction === 'out' ? (str(data.deliveryError) || null) : null,
    reactions: data.reactions && typeof data.reactions === 'object' ? (data.reactions as Record<string, string>) : null,
    quotedId: str(data.quotedId) || null,
    quotedBody: str(data.quotedBody) || null,
    quotedDirection: data.quotedDirection === 'out' ? 'out' : data.quotedDirection === 'in' ? 'in' : null,
    albumId: str(data.albumId) || null,
    otp: data.otp === true,
    part: num(data.part) || null,
    parts: num(data.parts) || null,
  };
}

/** Inbound messages newer than the reader's mark — the unread pill. Counts
 *  from the summary's recent-inbound window (capped at its length), so a
 *  thread that was never opened still shows a number, not just a dot. */
export function unreadCountFor(t: SmsThreadSummary, readMark: number): number {
  const n = t.recentInboundAt.filter((at) => at > readMark).length;
  return n === 0 && t.lastInboundAt > readMark ? 1 : n;
}

const noop = () => {};

// ─── Supabase mode ──────────────────────────────────────────────────────────
// The same two queries, against the Postgres twins (migration 0011): the
// conversation list is `sms_threads`, an open thread is `forwarded_sms where
// thread_key =`. Reads are gated by RLS to the dispatch tier, and the OTP
// filter stays the client-side convention it is in Firestore.
//
// This replaced a 7-second poll of forwarded-sms-list, which could only ever
// see the newest few hundred rows line-wide — so a busy evening hid a
// customer's own history from their thread, and paging past the window
// reported "exhausted" when there was more.

const sbThread = (r: any): SmsThreadSummary => normalizeThread(r.thread_key, {
  threadKey: r.thread_key,
  phone: r.phone,
  otp: r.otp,
  lastMessageAt: r.last_message_at,
  lastMessageId: r.last_message_id,
  lastBody: r.last_body,
  lastDirection: r.last_direction,
  lastInboundAt: r.last_inbound_at,
  lastInboundId: r.last_inbound_id,
  recentInboundAt: r.recent_inbound_at,
  messageCount: r.message_count,
  historyOnly: r.history_only,
});

const sbMessage = (r: any): ForwardedSms => normalizeMessage(r.id, {
  direction: r.direction,
  from: r.from_number,
  to: r.to_number,
  threadKey: r.thread_key,
  body: r.body,
  sim: r.sim,
  receivedAt: r.received_at,
  loggedAt: r.logged_at,
  routedTo: r.routed_to,
  deliveryStatus: r.delivery_status,
  deliveryError: r.delivery_error,
  reactions: r.reactions,
  quotedId: r.quoted_id,
  quotedBody: r.quoted_body,
  quotedDirection: r.quoted_direction,
  albumId: r.album_id,
  otp: r.otp,
  part: r.part,
  parts: r.parts,
});

/** Firestore's onSnapshot contract over a Postgres query, for the reads that
 *  are NOT a plain table window — currently none: both live reads below now go
 *  through supabaseClient.subscribeTable, which patches each realtime payload
 *  into the rows it already holds and paints from a persisted cache first.
 *
 *  WHY THIS CHANGED (2026-09-09). The old helper here threw the payload away
 *  and re-ran the whole query on any change to the table: 100 thread rows for
 *  the list, 30 for the open thread, 250 ms after every single text — and the
 *  list subscription runs twice (this page and NotificationHandler), in every
 *  open tab. Measured against the real schema that is 124 KB of JSON to deliver
 *  one 694-byte message. subscribeTable applies the row it was handed instead,
 *  so the same text costs one websocket frame and a map write.
 */

// One shared conversation-list store for the whole app.
//
// The list is read in two places — the Text Inbox and the unread badge in
// NotificationHandler, which is mounted on EVERY page. Two independent
// subscriptions meant two channels, two caches and two full re-reads per text.
// This store subscribes once, refcounted, and fans the rows out to both.
type ThreadsListener = (threads: SmsThreadSummary[]) => void;
type ErrListener = (e: Error) => void;

interface ThreadsHub {
  listeners: Set<ThreadsListener>;
  errListeners: Set<ErrListener>;
  latest: SmsThreadSummary[] | null;
  off: (() => void) | null;
  /** The schema probe is in flight — do not open a second subscription. */
  starting: boolean;
}
const threadHubs = new Map<boolean, ThreadsHub>(); // keyed by seesOtp

function threadsHub(seesOtp: boolean): ThreadsHub {
  let hub = threadHubs.get(seesOtp);
  if (!hub) {
    hub = { listeners: new Set(), errListeners: new Set(), latest: null, off: null, starting: false };
    threadHubs.set(seesOtp, hub);
  }
  return hub;
}

/** Shared, refcounted subscription to the conversation list. */
function subscribeThreadsShared(
  seesOtp: boolean,
  cb: ThreadsListener,
  onError?: ErrListener,
): () => void {
  const hub = threadsHub(seesOtp);
  hub.listeners.add(cb);
  if (onError) hub.errListeners.add(onError);
  // A late subscriber gets the rows the hub already holds, synchronously —
  // switching to the inbox from another page paints with no round-trip.
  if (hub.latest) cb(hub.latest);

  if (!hub.off && !hub.starting) {
    hub.starting = true;
    // The probe decides the column list. `starting` keeps a second subscriber
    // from opening a second channel while it is in flight.
    void hasHistoryOnlyColumn().then((withHistory) => {
      hub.starting = false;
      if (!hub.listeners.size) return; // everyone left while we were probing
      hub.off = subscribeTable<SmsThreadSummary>(
        THREADS_TABLE,
        sbThread,
        (rows) => { hub!.latest = rows; hub!.listeners.forEach((l) => l(rows)); },
        {
          pk: 'thread_key',
          // Every column except updated_at is rendered (the ids feed the unread
          // pill and the alert de-dupe), so this is a small win — but a free one.
          select: withHistory ? THREAD_COLUMNS : THREAD_COLUMNS_BASE,
          order: { column: 'last_message_at', ascending: false },
          limit: THREAD_PAGE,
          // Query + accept filter only, never a channel filter: a thread that
          // becomes OTP must still deliver the payload that removes it here.
          ...(seesOtp ? {} : { eq: [{ column: 'otp', value: false }] }),
          onError: (e) => hub!.errListeners.forEach((l) => l(e)),
        },
      );
    });
  }

  return () => {
    hub.listeners.delete(cb);
    if (onError) hub.errListeners.delete(onError);
    if (!hub.listeners.size && hub.off) { hub.off(); hub.off = null; }
  };
}

/** Test seam: drop the shared subscription and its cached rows. */
export function _resetThreadHubs(): void {
  for (const hub of threadHubs.values()) { if (hub.off) hub.off(); }
  threadHubs.clear();
  _resetHistoryColumnProbe();
}

// ────────────────────────────────────────────────────────────────────────────

function threadsQuery(seesOtp: boolean): Query {
  let q: Query = db!.collection(THREADS_COL);
  if (!seesOtp) q = q.where('otp', '==', false);
  return q.orderBy('lastMessageAt', 'desc');
}

function messagesQuery(threadKey: string, seesOtp: boolean): Query {
  let q: Query = db!.collection(MESSAGES_COL).where('threadKey', '==', threadKey);
  if (!seesOtp) q = q.where('otp', '==', false);
  return q.orderBy('receivedAt', 'desc');
}

/** Live conversation list, newest first, first page only. */
export function subscribeThreads(
  seesOtp: boolean,
  cb: (threads: SmsThreadSummary[]) => void,
  onError?: (e: Error) => void,
): () => void {
  if (USE_SUPABASE) return subscribeThreadsShared(seesOtp, cb, onError);
  if (!db) { cb([]); return noop; }
  return threadsQuery(seesOtp).limit(THREAD_PAGE).onSnapshot(
    (snap) => cb(snap.docs.map((d) => normalizeThread(d.id, d.data() as Data))),
    (err) => onError?.(err as Error),
  );
}

/** Next page of conversations older than `beforeLastMessageAt`. */
export async function loadMoreThreads(seesOtp: boolean, beforeLastMessageAt: number): Promise<SmsThreadSummary[]> {
  if (USE_SUPABASE) {
    let q = supabase().from(THREADS_TABLE).select('*');
    if (!seesOtp) q = q.eq('otp', false);
    const { data, error } = await q
      .lt('last_message_at', beforeLastMessageAt)
      .order('last_message_at', { ascending: false })
      .limit(THREAD_PAGE);
    if (error) throw new Error(error.message);
    return (data || []).map(sbThread);
  }
  if (!db) return [];
  const snap = await threadsQuery(seesOtp).startAfter(beforeLastMessageAt).limit(THREAD_PAGE).get();
  return snap.docs.map((d) => normalizeThread(d.id, d.data() as Data));
}

/** Live newest window of one thread, delivered oldest → newest. */
export function subscribeThreadMessages(
  threadKey: string,
  seesOtp: boolean,
  cb: (messages: ForwardedSms[]) => void,
  onError?: (e: Error) => void,
): () => void {
  if (USE_SUPABASE) {
    if (!threadKey) { cb([]); return noop; }
    // `eq` narrows BOTH the query and the realtime channel, so a text for
    // another customer never wakes the open conversation. Rows arrive newest
    // first (the indexed order) and are reversed for the UI, which reads
    // oldest → newest.
    return subscribeTable<ForwardedSms>(
      MESSAGES_TABLE,
      sbMessage,
      (rows) => cb([...rows].reverse()),
      {
        pk: 'id',
        eq: seesOtp
          ? [{ column: 'thread_key', value: threadKey }]
          : [{ column: 'thread_key', value: threadKey }, { column: 'otp', value: false }],
        // Safe to narrow the channel on thread_key: a message never moves
        // between conversations, so no payload is hidden by doing so. This is
        // the one that matters — without it every text on the line wakes every
        // open conversation.
        channelFilter: `thread_key=eq.${threadKey}`,
        order: { column: 'received_at', ascending: false },
        limit: MESSAGE_PAGE,
        onError,
      },
    );
  }
  if (!db || !threadKey) { cb([]); return noop; }
  return messagesQuery(threadKey, seesOtp).limit(MESSAGE_PAGE).onSnapshot(
    (snap) => cb(snap.docs.map((d) => normalizeMessage(d.id, d.data() as Data)).reverse()),
    (err) => onError?.(err as Error),
  );
}

/** One page of older messages, oldest → newest.
 *
 *  KEYSET, WITH A TIEBREAKER (2026-09-09). `received_at` is not unique — a
 *  multi-part text and a burst both land on the same millisecond — so paging on
 *  `received_at < cursor` alone SILENTLY DROPS every sibling sharing the page
 *  boundary's timestamp. Messages went missing from long threads, and the more
 *  traffic a customer had the likelier it was. The cursor is therefore the pair
 *  (received_at, id), matching the sort, exactly as keyset pagination requires.
 *
 *  `before.id` is optional: the Firestore path still pages on the timestamp
 *  alone (a two-field cursor there needs a composite index this legacy backend
 *  does not have, and Postgres is where the volume lives). */
export async function loadOlderMessages(
  threadKey: string,
  seesOtp: boolean,
  before: { receivedAt: number; id?: string | null },
  pageSize: number = MESSAGE_PAGE,
): Promise<ForwardedSms[]> {
  if (USE_SUPABASE) {
    if (!threadKey) return [];
    let q = supabase().from(MESSAGES_TABLE).select('*').eq('thread_key', threadKey);
    if (!seesOtp) q = q.eq('otp', false);
    // (received_at, id) < (cursor.receivedAt, cursor.id), as PostgREST spells it.
    q = before.id
      ? q.or(`received_at.lt.${before.receivedAt},and(received_at.eq.${before.receivedAt},id.lt.${before.id})`)
      : q.lt('received_at', before.receivedAt);
    const { data, error } = await q
      .order('received_at', { ascending: false })
      .order('id', { ascending: false })
      .limit(pageSize);
    if (error) throw new Error(error.message);
    return (data || []).map(sbMessage).reverse();
  }
  if (!db || !threadKey) return [];
  const snap = await messagesQuery(threadKey, seesOtp).startAfter(before.receivedAt).limit(pageSize).get();
  return snap.docs.map((d) => normalizeMessage(d.id, d.data() as Data)).reverse();
}

/** Merge rows into an id-keyed map — the live window, older pages and the
 *  optimistic outbox never wipe each other. Returns a new Map when anything
 *  changed, the same instance otherwise (cheap React identity checks). */
export function mergeMessages(
  current: Map<string, ForwardedSms>,
  incoming: ForwardedSms[],
): Map<string, ForwardedSms> {
  let next: Map<string, ForwardedSms> | null = null;
  for (const m of incoming) {
    const cur = current.get(m.id);
    if (cur && sameRow(cur, m)) continue;
    if (!next) next = new Map(current);
    next.set(m.id, m);
  }
  return next || current;
}

function sameRow(a: ForwardedSms, b: ForwardedSms): boolean {
  return a.body === b.body && a.receivedAt === b.receivedAt && a.deliveryStatus === b.deliveryStatus
    && a.deliveryError === b.deliveryError && a.routedTo === b.routedTo
    && JSON.stringify(a.reactions) === JSON.stringify(b.reactions);
}

/** Results per search. WhatsApp-sized: enough to pick from, never the whole list. */
export const SEARCH_LIMIT = 50;

/** One thread summary by key (a search hit or a deep link that isn't in the
 *  loaded list). Resolves null when the conversation doesn't exist yet. */
export async function fetchThread(threadKey: string): Promise<SmsThreadSummary | null> {
  if (USE_SUPABASE) {
    if (!threadKey) return null;
    const { data, error } = await supabase()
      .from(THREADS_TABLE).select('*').eq('thread_key', threadKey).maybeSingle();
    if (error) throw new Error(error.message);
    return data ? sbThread(data) : null;
  }
  if (!db || !threadKey) return null;
  const snap = await db.collection(THREADS_COL).doc(threadKey).get();
  return snap.exists ? normalizeThread(snap.id, (snap.data() || {}) as Data) : null;
}

/** Conversations whose NUMBER contains `digits` — any part of it: the area
 *  code, the middle, or the last four (how staff actually look a customer up).
 *
 *  This was prefix-only while the inbox lived on Firestore, where "contains"
 *  is not queryable. On Postgres it is one `like`, so a search for "2399" now
 *  reaches every conversation however old, not just the page already loaded.
 *  ONE request: newest conversations first, prefix hits ranked ahead of
 *  mid-number hits. A complete 10-digit number stays an index-friendly prefix
 *  match. Migration 0069 adds a trigram index so the contains scan stays fast
 *  as the table grows; without it the query is still correct.
 *
 *  The Firestore path remains prefix-only (a range on the document id). */
/** Digits typed into search → the thread-key prefix they mean. A leading 1 is
 *  the country code (no NA area code starts with 1), so "1613" means "613". */
export function searchPrefix(query: string): string {
  const d = normalizePhone(query);
  return d.length > 1 && d.length <= 11 && d.startsWith('1') ? d.slice(1) : d;
}

/** Pure: does this conversation's number contain the digits typed? Punctuation
 *  on either side is ignored ("613-410" finds (613) 410-2399), and a leading
 *  country code works ("1613…"). False when the query has no digits. */
export function numberMatchesQuery(number: string, query: string): boolean {
  const d = String(query || '').replace(/\D/g, '');
  if (!d) return false;
  const key = normalizePhone(number);
  if (!key) return false;
  return `1${key}`.includes(d) || key.includes(d);
}

/** True when the search text is a NUMBER lookup (digits and phone punctuation
 *  only) rather than a name that happens to contain a digit. */
export function isNumberQuery(query: string): boolean {
  const q = String(query || '').trim();
  return /\d/.test(q) && /^[\d\s()+\-.]+$/.test(q);
}

export async function searchThreadsByNumber(digits: string, limit: number = SEARCH_LIMIT): Promise<SmsThreadSummary[]> {
  const prefix = searchPrefix(digits);
  if (USE_SUPABASE) {
    if (!prefix) return [];
    const raw = String(digits || '').replace(/\D/g, '');
    let q = supabase().from(THREADS_TABLE).select(THREAD_COLUMNS_BASE);
    if (prefix.length >= 10) {
      // A whole number: prefix match, served by the primary-key index.
      q = q.like('thread_key', `${prefix}%`);
    } else if (raw !== prefix) {
      // Leading 1 is ambiguous: the country code ("1613" → 613…) or plain
      // digits from the middle of a number ("1234"). Ask for both at once.
      q = q.or(`thread_key.like.${prefix}%,thread_key.like.%${raw}%`);
    } else {
      q = q.like('thread_key', `%${prefix}%`);
    }
    const { data, error } = await q.order('last_message_at', { ascending: false }).limit(limit);
    if (error) throw new Error(error.message);
    const rows = (data || []).map(sbThread);
    // Stable: prefix hits first, each group still newest-first.
    const starts = (t: SmsThreadSummary) => (t.threadKey.startsWith(prefix) ? 0 : 1);
    return rows.map((t, i) => ({ t, i })).sort((a, b) => starts(a.t) - starts(b.t) || a.i - b.i).map((x) => x.t);
  }
  if (!db || !prefix) return [];
  const id = firebase.firestore.FieldPath.documentId();
  const snap = await db.collection(THREADS_COL)
    .where(id, '>=', prefix)
    .where(id, '<', `${prefix}\uf8ff`)
    .limit(limit)
    .get();
  return snap.docs.map((d) => normalizeThread(d.id, d.data() as Data));
}

/** Pure: contacts whose name contains `query` (case-insensitive) or whose
 *  number starts with its digits. Contacts are already in memory on the inbox
 *  page, so name search costs no reads. Exported for tests. */
export function searchContacts(contacts: Iterable<SmsContact>, query: string, limit: number = SEARCH_LIMIT): SmsContact[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const digits = searchPrefix(q);
  const out: SmsContact[] = [];
  for (const c of contacts) {
    const nameHit = !!c.name && c.name.toLowerCase().includes(q);
    const numberHit = !!digits && normalizePhone(c.phone).startsWith(digits);
    if (nameHit || numberHit) { out.push(c); if (out.length >= limit) break; }
  }
  return out;
}
